// src/components/Experiment/VisualizationCard.js
import React, { useState, useRef } from 'react';
import { Card, Button, Modal, Badge, Spinner, Alert } from 'react-bootstrap';
import useImageWithAuth from "../../hooks/useImageWithAuth";
import HelpLink from '../HelpLink';
import { PLOT_DOCS } from '../../constants/docsLinks';
import './VisualizationCard.css';

const VisualizationCard = ({ visualization, onDelete }) => {
  const [showImageModal, setShowImageModal] = useState(false);
  const [image, imageStatus, imageError] = useImageWithAuth('analysis', visualization.image_file);
  const imageRef = useRef(null);

  const getPlotTypeDisplay = (type) => {
    const types = {
      'score_plot': 'Score Plot',
      'loading_plot': 'Loading Plot',
      'time_series': 'Time Series',
      'heatmap': 'Heatmap',
      'confusion_matrix': 'Confusion Matrix',
      'roc_curve': 'ROC Curve',
      'feature_importance': 'Feature Importance',
      'knn_distance': 'KNN Distance',
      'plsda_loading': 'PLS-DA Loading',
      'permutation_plot': 'Permutation Plot',
      'bootstrap_ci': 'Bootstrap CI'
    };
    return types[type] || type;
  };

  // Permutation-plot p-values (only present for plot_type === 'permutation_plot').
  // Falls back to visualization.data for backward compatibility if the API
  // has not been updated yet.
  const plotDoc = PLOT_DOCS[visualization.plot_type];

  const permStats = visualization.permutation_stats || visualization.data || {};
  const isPermutationPlot = visualization.plot_type === 'permutation_plot';
  const trainPValue = permStats.train_p_value;
  const cvPValue = permStats.cv_p_value;
  const hasPValues =
    isPermutationPlot &&
    ((trainPValue !== null && trainPValue !== undefined) ||
      (cvPValue !== null && cvPValue !== undefined));

  // Bootstrap CI summary (only present for plot_type === 'bootstrap_ci').
  const bootStats = visualization.plot_type === 'bootstrap_ci' ? visualization.bootstrap_stats : null;
  const bootPct = Math.round(100 * (bootStats?.level ?? 0.95));

  const formatInterval = (summary) => {
    if (!summary || summary.mean === null || summary.mean === undefined) return '—';
    return `${Number(summary.mean).toFixed(2)} [${Number(summary.low).toFixed(2)}; ${Number(summary.high).toFixed(2)}]`;
  };

  const formatPValue = (value) => {
    if (value === null || value === undefined) return '—';
    return Number(value).toFixed(4);
  };

  // Conventional significance level: p <= 0.05 is shown in green. The smallest
  // reachable p-value is 1 / (n_permutations + 1), so green needs at least
  // 19 permutations (the dialog allows 50-500).
  const P_VALUE_THRESHOLD = 0.05;

  const isSignificant = (value) =>
    value !== null && value !== undefined && Number(value) <= P_VALUE_THRESHOLD;

  const pValueClass = (value) => {
    if (value === null || value === undefined) return 'stat-value';
    return `stat-value ${isSignificant(value) ? 'is-significant' : 'is-nonsignificant'}`;
  };

  // Function to handle download - using the image object from useImageWithAuth
  const handleDownload = () => {
    if (image && image.src) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = image.src;

      // Extract filename from URL or use visualization name
      const filename = visualization.image_file?.split('/').pop() ||
                      `${visualization.name.replace(/\s+/g, '_')}.jpg`;

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Function to get status badge variant
  const getStatusVariant = () => {
    switch (visualization.status) {
      case 'completed': return 'success';
      case 'running': return 'primary';
      case 'failed': return 'danger';
      case 'pending': return 'secondary';
      default: return 'secondary';
    }
  };

  // Function to get status display text
  const getStatusDisplay = () => {
    switch (visualization.status) {
      case 'completed': return 'Completed';
      case 'running': return 'Running';
      case 'failed': return 'Failed';
      case 'pending': return 'Pending';
      default: return visualization.status;
    }
  };

  return (
    <>
      <Card className="visualization-card h-100">
        <Card.Body className="p-3">
          {/* Header with title and controls */}
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="flex-grow-1">
              <h6 className="mb-1 fw-bold">{visualization.name}</h6>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="d-inline-flex align-items-center">
                  <Badge bg="secondary">
                    {getPlotTypeDisplay(visualization.plot_type)}
                  </Badge>
                  {plotDoc && (
                    <HelpLink
                      path={plotDoc}
                      title={`Documentation: ${getPlotTypeDisplay(visualization.plot_type)}`}
                    />
                  )}
                </span>
                {visualization.plot_type === 'confusion_matrix' && (
                  <Badge bg="light" text="dark" className="border">
                    {visualization.parameters?.source === 'cv' ? 'Cross-validation' : 'Training (self-test)'}
                  </Badge>
                )}
                <Badge bg={getStatusVariant()}>
                  {getStatusDisplay()}
                </Badge>
                <small className="text-muted">
                  {new Date(visualization.created_at).toLocaleDateString()}
                </small>
              </div>
            </div>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={onDelete}
              title="Delete visualization"
              className="flex-shrink-0"
            >
              <i className="bi bi-trash"></i>
            </Button>
          </div>

          {/* Error Message Display */}
          {visualization.error_message && visualization.error_message.length > 0 && (
            <Alert variant="danger" className="mb-3 small">
              <strong>Error:</strong> {visualization.error_message}
            </Alert>
          )}

          {/* Permutation-plot p-values */}
          {hasPValues && (
            <div
              className="visualization-stats"
              title="p-value = (number of permutations whose metric is higher than or equal to that of the original model + 1) / (number of permutations + 1)"
            >
              <div className="stat">
                <span className="stat-label">p-value</span>
                <span className={pValueClass(trainPValue)}>{formatPValue(trainPValue)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">CV p-value</span>
                <span className={pValueClass(cvPValue)}>{formatPValue(cvPValue)}</span>
              </div>
              {(permStats.metric || permStats.n_permutations) && (
                <div className="stat stat-meta">
                  <span className="stat-label">Test</span>
                  <span className="stat-value">
                    {permStats.metric ? String(permStats.metric).replace('_', ' ').toUpperCase() : '—'}
                    {permStats.n_classes > 2 ? ` · ${permStats.n_classes} classes` : ''}
                    {permStats.n_permutations ? ` · n=${permStats.n_permutations}` : ''}
                    {permStats.feature_selection_mode
                      ? ` · FS: ${permStats.feature_selection_mode === 'per_permutation' ? 'per permutation' : 'once'}`
                      : ''}
                    {permStats.nested ? ' · nested CV' : ''}
                    {permStats.stopped_early ? ` · stopped early after ${permStats.n_done}` : ''}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Bootstrap CI: mean [percentile interval] */}
          {bootStats && (
            <div
              className="visualization-stats"
              title={`Mean of the out-of-bag bootstrap values [${bootPct}% percentile interval]. Iterations whose out-of-bag part had one class only are skipped.`}
            >
              <div className="stat">
                <span className="stat-label">AUC</span>
                <span className="stat-value">{formatInterval(bootStats.auc)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Accuracy</span>
                <span className="stat-value">{formatInterval(bootStats.accuracy)}</span>
              </div>
              {/* Absent in bootstrap visualizations created before they were added */}
              {[
                ['Sensitivity', bootStats.sensitivity,
                  bootStats.positive_class != null ? `Positive class: ${bootStats.positive_class}` : null],
                ['Specificity', bootStats.specificity, null],
                ['Bal. accuracy', bootStats.balanced_accuracy, null],
                ['Precision', bootStats.precision_macro, 'Macro average over the classes'],
                ['F1', bootStats.f1_macro, 'Macro average over the classes'],
              ].filter(([, summary]) => summary).map(([label, summary, hint]) => (
                <div className="stat" key={label} title={hint || undefined}>
                  <span className="stat-label">{label}</span>
                  <span className="stat-value">{formatInterval(summary)}</span>
                </div>
              ))}
              <div className="stat stat-meta">
                <span className="stat-label">Bootstrap</span>
                <span className="stat-value">
                  {`n=${bootStats.n_used ?? '—'}/${bootStats.n_iterations ?? '—'}`}
                  {bootStats.n_skipped ? ` · skipped ${bootStats.n_skipped}` : ''}
                  {bootStats.n_failed ? ` · failed ${bootStats.n_failed}` : ''}
                  {bootStats.random_state !== null && bootStats.random_state !== undefined
                    ? ` · seed ${bootStats.random_state}` : ''}
                  {bootStats.unit === 'rows_adjusted' ? ' · by sample' : ''}
                  {bootStats.feature_selection_mode
                    ? ` · FS: ${bootStats.feature_selection_mode === 'per_iteration' ? 'per iteration' : 'once'}`
                    : ''}
                </span>
              </div>
              {bootStats.warning && (
                <div className="stat stat-meta">
                  <span className="stat-label">Warning</span>
                  <span className="stat-value text-warning">{bootStats.warning}</span>
                </div>
              )}
            </div>
          )}

          {/* Image Container */}
          <div className="visualization-image-container">
            {visualization.image_file ? (
              <div className="text-center">
                {imageStatus === 'loading' && (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                    <Spinner animation="border" variant="primary" />
                    <span className="ms-2">Loading visualization...</span>
                  </div>
                )}
                {imageStatus === 'loaded' && image && (
                  <img
                    ref={imageRef}
                    src={image.src}
                    alt={visualization.name}
                    className="visualization-image img-fluid rounded"
                    onClick={() => setShowImageModal(true)}
                    style={{
                      cursor: 'pointer',
                      maxWidth: '100%',
                      height: 'auto',
                      maxHeight: '400px',
                      objectFit: 'contain'
                    }}
                  />
                )}
                {imageStatus === 'failed' && (
                  <div className="text-danger d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                    <div>
                      <i className="bi bi-exclamation-triangle fs-1"></i>
                      <div>Failed to load image</div>
                      {imageError && <small>{imageError}</small>}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // No image file case
              <div className="text-center d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                {visualization.status === 'running' && (
                  <div>
                    <Spinner animation="border" variant="primary" />
                    <div className="mt-2">Generating visualization...</div>
                  </div>
                )}
                {visualization.status === 'pending' && (
                  <div className="text-muted">
                    <i className="bi bi-clock fs-1"></i>
                    <div>Visualization pending</div>
                  </div>
                )}
                {visualization.status === 'failed' && (
                  <div className="text-danger">
                    <i className="bi bi-x-circle fs-1"></i>
                    <div>Visualization failed</div>
                  </div>
                )}
                {visualization.status === 'completed' && !visualization.image_file && (
                  <div className="text-warning">
                    <i className="bi bi-exclamation-triangle fs-1"></i>
                    <div>No image generated</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowImageModal(true)}
              disabled={!image || imageStatus !== 'loaded'}
            >
              <i className="bi bi-zoom-in me-1"></i>
              View Full Size
            </Button>
            {image && imageStatus === 'loaded' && (
              <Button
                variant="outline-success"
                size="sm"
                onClick={handleDownload}
              >
                <i className="bi bi-download me-1"></i>
                Download
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Full Size Image Modal */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="xl"
        centered
        className="visualization-modal"
      >
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6">{visualization.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0 d-flex justify-content-center align-items-center bg-light">
          {imageStatus === 'loading' && (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
              <Spinner animation="border" variant="primary" />
              <span className="ms-2">Loading image...</span>
            </div>
          )}
          {imageStatus === 'loaded' && image && (
            <div className="w-100 h-100 d-flex justify-content-center align-items-center p-3">
              <img
                src={image.src}
                alt={visualization.name}
                className="img-fluid"
                style={{
                  maxHeight: '75vh',
                  maxWidth: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain'
                }}
              />
            </div>
          )}
          {imageStatus === 'failed' && (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
              <div className="text-danger text-center">
                <i className="bi bi-exclamation-triangle-fill fs-1"></i>
                <div>Failed to load image</div>
                {imageError && <small>{imageError}</small>}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div>
              <Badge bg="info" className="me-2">
                {getPlotTypeDisplay(visualization.plot_type)}
              </Badge>
              <Badge bg={getStatusVariant()}>
                {getStatusDisplay()}
              </Badge>
            </div>
            <div>
              {image && imageStatus === 'loaded' && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleDownload}
                  className="me-2"
                >
                  <i className="bi bi-download me-1"></i>
                  Download
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={() => setShowImageModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VisualizationCard;